import type { GetGeoJSONFeatureOpacityHandler } from "./mapLayers";

/** Minimum opacity for foundation features */
const MIN_OPACITY = 0.1;

/** Maximum opacity for foundation features */
const MAX_OPACITY = 0.6;

/**
 * Parses percentage string (e.g., "60-80 %") and returns the average value
 * @param percentageStr - Percentage string in format "XX-XX %" or "XX %"
 * @returns Average percentage as a number, or null if parsing fails
 */
const parsePercentageRange = (percentageStr: string): number | null => {
  // Remove whitespace and the % sign
  const cleaned = percentageStr.trim().replace(/\s*%\s*$/, "");

  // Match patterns like "60-80" or "60 - 80"
  const rangeMatch = cleaned.match(/^(\d+)\s*-\s*(\d+)$/);
  if (rangeMatch) {
    const low = parseInt(rangeMatch[1], 10);
    const high = parseInt(rangeMatch[2], 10);
    if (!Number.isNaN(low) && !Number.isNaN(high)) {
      return (low + high) / 2;
    }
  }

  // Try single number (e.g., "80 %")
  const singleMatch = cleaned.match(/^(\d+)$/);
  if (singleMatch) {
    const value = parseInt(singleMatch[1], 10);
    if (!Number.isNaN(value)) {
      return value;
    }
  }

  return null;
};

/**
 * Generates opacity for foundation GeoJSON features based on percentage value.
 * Parses the legenda property (format: "Area - XX-XX %") and maps percentage to opacity.
 * Higher percentage = higher opacity (more visible).
 *
 * @param properties - GeoJSON feature properties
 * @returns Opacity value between 0.1 and 0.6, or null to use default
 */
export const getFoundationFeatureOpacity: GetGeoJSONFeatureOpacityHandler = (
  properties,
) => {
  if (!properties.legenda) {
    return null;
  }

  const legendaStr = String(properties.legenda);
  const legendaParts = legendaStr.split(" - ");

  if (legendaParts.length !== 2) {
    return null;
  }

  const [, percentage] = legendaParts;
  const percentageValue = parsePercentageRange(percentage);

  if (percentageValue === null) {
    return null;
  }

  // Map percentage (0-100) to opacity (MIN_OPACITY-MAX_OPACITY)
  const opacity =
    MIN_OPACITY + (percentageValue / 100) * (MAX_OPACITY - MIN_OPACITY);

  return Math.max(MIN_OPACITY, Math.min(MAX_OPACITY, opacity));
};
